import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { partyInfo } from '../mockData';

const Contact = () => {
  const { language, t } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed');
      toast({
        title: t({ en: 'Message Sent!', bn: 'বার্তা পাঠানো হয়েছে!' }),
        description: t({
          en: 'Thank you for contacting us. We will get back to you soon.',
          bn: 'আমাদের সাথে যোগাযোগ করার জন্য ধন্যবাদ। আমরা শীঘ্রই আপনার সাথে যোগাযোগ করব।',
        }),
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast({
        title: t({ en: 'Error', bn: 'ত্রুটি' }),
        description: t({
          en: 'Failed to send message. Please try again.',
          bn: 'বার্তা পাঠাতে ব্যর্থ। অনুগ্রহ করে আবার চেষ্টা করুন।',
        }),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-party-green py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t({ en: 'Contact Us', bn: 'যোগাযোগ করুন' })}
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            {t({
              en: 'Get in touch with us. We are here to listen and serve.',
              bn: 'আমাদের সাথে যোগাযোগ করুন। আমরা শুনতে এবং সেবা করতে এখানে আছি।',
            })}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                {t({ en: 'Get In Touch', bn: 'যোগাযোগ করুন' })}
              </h2>
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-party-red">
                      <MapPin className="h-5 w-5 mr-2" />
                      {t({ en: 'Address', bn: 'ঠিকানা' })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{t(partyInfo.address)}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-party-red">
                      <Phone className="h-5 w-5 mr-2" />
                      {t({ en: 'Phone', bn: 'ফোন' })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {partyInfo.phones.map((phone, idx) => (
                      <a key={idx} href={`tel:${phone.replace(/\s/g, '')}`} className="block text-gray-600 hover:text-party-green transition-colors">
                        {phone}
                      </a>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-party-red">
                      <Mail className="h-5 w-5 mr-2" />
                      {t({ en: 'Email', bn: 'ইমেইল' })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <a href={`mailto:${partyInfo.email}`} className="text-gray-600 hover:text-party-red transition-colors underline" data-testid="contact-email-link">
                      {partyInfo.email}
                    </a>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-party-red">
                      <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      {t({ en: 'Follow Us', bn: 'আমাদের অনুসরণ করুন' })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <a href="https://www.facebook.com/profile.php?id=100066695862611" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1877F2] text-white font-medium hover:bg-[#166FE5] transition-colors" data-testid="contact-facebook-link">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      {t({ en: 'NDP on Facebook', bn: 'ফেসবুকে এনডিপি' })}
                    </a>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-party-red">
                      <Clock className="h-5 w-5 mr-2" />
                      {t({ en: 'Office Hours', bn: 'অফিস সময়' })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      {t({ en: 'Sunday - Thursday: 9:00 AM - 5:00 PM', bn: 'রবিবার - বৃহস্পতিবার: সকাল ৯টা - বিকাল ৫টা' })}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">
                  {t({ en: 'Send Us a Message', bn: 'আমাদের একটি বার্তা পাঠান' })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">{t({ en: 'Full Name', bn: 'পূর্ণ নাম' })} *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">{t({ en: 'Email', bn: 'ইমেইল' })} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">{t({ en: 'Phone Number', bn: 'ফোন নম্বর' })}</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject">{t({ en: 'Subject', bn: 'বিষয়' })} *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">{t({ en: 'Message', bn: 'বার্তা' })} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="mt-1"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-party-red hover:bg-party-red-600">
                    {t({ en: 'Send Message', bn: 'বার্তা পাঠান' })}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;